import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { track, getSource } from "@/track";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryCodes = [
  { code: "+1", label: "+1 (Canada / U.S.)" },
  { code: "+91", label: "+91 (India)" },
  { code: "+44", label: "+44 (U.K.)" },
  { code: "+61", label: "+61 (Australia)" },
  { code: "+971", label: "+971 (U.A.E.)" },
  { code: "+55", label: "+55 (Brazil)" },
  { code: "+52", label: "+52 (Mexico)" },
  { code: "+49", label: "+49 (Germany)" },
  { code: "+33", label: "+33 (France)" },
  { code: "+86", label: "+86 (China)" },
  { code: "+81", label: "+81 (Japan)" },
  { code: "+234", label: "+234 (Nigeria)" },
  { code: "+92", label: "+92 (Pakistan)" },
  { code: "+63", label: "+63 (Philippines)" },
];

const visaCategories = [
  "B1/B2 — Visitor / Business / Tourism",
  "F-1 — Student",
  "H-1B — Specialty Occupation",
  "L-1 — Intracompany Transfer",
  "O-1 — Extraordinary Ability",
  "J-1 — Exchange Visitor",
  "C1/D — Transit / Crew",
  "K-1 — Fiancé(e)",
  "Other",
];

const provinces = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Northwest Territories",
  "Nova Scotia",
  "Nunavut",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Yukon",
];

const initialForm = {
  full_name: "",
  email: "",
  country_code: "+1",
  phone: "",
  province: "",
  visa_category: "",
  locations: "",
  message: "",
  website: "",
};

export default function EnquiryForm() {
  const { lang, t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // pre-select +91 when the site is viewed in Hindi (only while the default is untouched)
  useEffect(() => {
    setForm((f) => {
      if (lang === "hi" && f.country_code === "+1") return { ...f, country_code: "+91" };
      if (lang !== "hi" && f.country_code === "+91") return { ...f, country_code: "+1" };
      return f;
    });
  }, [lang]);

  useEffect(() => {
    const applyPlan = () => {
      const prefill = sessionStorage.getItem("ea_prefill");
      if (!prefill) return;
      setForm((f) => ({ ...f, message: f.message || prefill }));
      sessionStorage.removeItem("ea_prefill");
    };
    applyPlan();
    window.addEventListener("hashchange", applyPlan);
    return () => window.removeEventListener("hashchange", applyPlan);
  }, []);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const isQuebec = form.province === "Quebec";

  const nextStep = () => {
    if (step === 0 && isQuebec) return;
    const fields = document.querySelectorAll(`[data-form-step="${step}"] [required]`);
    for (const f of fields) {
      if (!f.reportValidity()) return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { province, ...payload } = form;
      await axios.post(`${API}/contact-requests`, { ...payload, source: getSource() });
      track("form_submit", form.visa_category);
      setSubmitted(true);
      setForm(initialForm);
      setStep(0);
      toast.success(t("toast_ok"));
    } catch (err) {
      toast.error(t("toast_err"));
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [t("fm_step1"), t("fm_step2"), t("fm_step3")];

  if (submitted) {
    return (
      <div
        className="h-full min-h-[24rem] flex flex-col items-center justify-center text-center border border-brand/25 bg-navy-800 p-12"
        data-testid="contact-success-panel"
      >
        <CheckCircle2 size={52} className="text-brand" />
        <h3 className="mt-8 font-serif font-semibold text-3xl text-cream">{t("success_h")}</h3>
        <p className="mt-4 max-w-md text-sm text-cream/55 leading-relaxed">{t("success_x")}</p>
        <button
          onClick={() => setSubmitted(false)}
          data-testid="contact-submit-another-btn"
          className="mt-10 border border-brand/40 text-brand text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-brand/10 transition-colors duration-300"
        >
          {t("success_btn")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-brand/15 bg-navy-800 p-8 sm:p-10 lg:p-14" data-testid="contact-form">
      <div className="mb-12" data-testid="form-progress">
        <div className="flex items-center">
          {steps.map((label, i) => (
            <div key={label} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}>
              <div className="flex items-center gap-3">
                <span
                  className={`flex items-center justify-center w-9 h-9 rounded-full border text-xs font-bold transition-colors duration-300 ${
                    i < step
                      ? "bg-brand border-brand text-white"
                      : i === step
                      ? "border-brand text-brand-300"
                      : "border-brand/20 text-cream/30"
                  }`}
                  data-testid={`form-step-dot-${i}`}
                >
                  {i < step ? <Check size={15} /> : i + 1}
                </span>
                <span
                  className={`hidden sm:block text-xs uppercase tracking-[0.15em] ${
                    i === step ? "text-cream" : "text-cream/35"
                  }`}
                >
                  {label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className="flex-1 h-px mx-3 sm:mx-5 bg-brand/15 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-brand transition-all duration-500"
                    style={{ width: i < step ? "100%" : "0%" }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-cream/35 sm:hidden">
          {t("step_label")} {step + 1} {t("step_of")} 3 — {steps[step]}
        </p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          {step === 0 && (
            <div data-form-step="0" className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
              <div>
                <label htmlFor="full_name" className="block text-[10px] uppercase tracking-[0.25em] text-brand mb-1">
                  {t("f_name")} <span className="text-cream/40">{t("f_req")}</span>
                </label>
                <input
                  id="full_name"
                  data-testid="contact-fullname-input"
                  className="brand-input"
                  value={form.full_name}
                  onChange={update("full_name")}
                  required
                  minLength={2}
                  placeholder={t("ph_name")}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-[10px] uppercase tracking-[0.25em] text-brand mb-1">
                  {t("f_email")} <span className="text-cream/40">{t("f_req")}</span>
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  className="brand-input"
                  value={form.email}
                  onChange={update("email")}
                  required
                  placeholder="you@example.com"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="phone" className="block text-[10px] uppercase tracking-[0.25em] text-brand mb-1">
                  {t("f_phone")} <span className="text-cream/40">{t("f_req")}</span>
                </label>
                <div className="flex gap-4">
                  <select
                    data-testid="contact-country-code-select"
                    className="brand-input w-40 flex-none cursor-pointer bg-navy-800"
                    value={form.country_code}
                    onChange={update("country_code")}
                    aria-label="Country code"
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.code} className="bg-navy-800">
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    id="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    className="brand-input"
                    value={form.phone}
                    onChange={update("phone")}
                    required
                    minLength={5}
                    placeholder="403 555 0123"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="province" className="block text-[10px] uppercase tracking-[0.25em] text-brand mb-1">
                  {t("f_prov")} <span className="text-cream/40">{t("f_req")}</span>
                </label>
                <select
                  id="province"
                  data-testid="contact-province-select"
                  className="brand-input cursor-pointer bg-navy-800"
                  value={form.province}
                  onChange={(e) => {
                    update("province")(e);
                    if (e.target.value === "Quebec") track("quebec_blocked");
                  }}
                  required
                >
                  <option value="" disabled className="bg-navy-800">{t("f_select_prov")}</option>
                  {provinces.map((p) => (
                    <option key={p} value={p} className="bg-navy-800">
                      {p === "Quebec" && lang === "fr" ? "Québec" : p}
                    </option>
                  ))}
                  <option value="Outside Canada" className="bg-navy-800">{t("prov_outside")}</option>
                </select>
                {isQuebec && (
                  <p className="mt-3 border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-200 leading-relaxed" data-testid="quebec-block-warning" role="alert">
                    {t("prov_qc_block")}
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-form-step="1" className="grid gap-y-8">
              <div>
                <label htmlFor="visa_category" className="block text-[10px] uppercase tracking-[0.25em] text-brand mb-1">
                  {t("f_cat")} <span className="text-cream/40">{t("f_req")}</span>
                </label>
                <select
                  id="visa_category"
                  data-testid="contact-visa-category-select"
                  className="brand-input cursor-pointer bg-navy-800"
                  value={form.visa_category}
                  onChange={update("visa_category")}
                  required
                >
                  <option value="" disabled className="bg-navy-800">{t("f_select_cat")}</option>
                  {visaCategories.map((v) => (
                    <option key={v} value={v} className="bg-navy-800">{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="locations" className="block text-[10px] uppercase tracking-[0.25em] text-brand mb-1">
                  {t("f_loc")} <span className="text-cream/40">{t("f_req")}</span>
                </label>
                <input
                  id="locations"
                  data-testid="contact-locations-input"
                  className="brand-input"
                  value={form.locations}
                  onChange={update("locations")}
                  required
                  minLength={2}
                  placeholder={t("ph_loc")}
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-[10px] uppercase tracking-[0.25em] text-brand mb-1">
                  {t("f_msg")}
                </label>
                <textarea
                  id="message"
                  rows={4}
                  data-testid="contact-message-input"
                  className="brand-input resize-none"
                  value={form.message}
                  onChange={update("message")}
                  placeholder={t("ph_msg")}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-form-step="2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-brand-300 mb-6">{t("rv_title")}</p>
              <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-6" data-testid="form-review">
                {[
                  [t("f_name"), form.full_name],
                  [t("f_email"), form.email],
                  [t("f_phone"), `${form.country_code} ${form.phone}`],
                  [t("f_prov"), form.province === "Outside Canada" ? t("prov_outside") : form.province],
                  [t("f_cat"), form.visa_category],
                  [t("f_loc"), form.locations],
                  [t("f_msg"), form.message || "—"],
                ].map(([label, value]) => (
                  <div key={label} className="border-b border-brand/10 pb-4">
                    <dt className="text-[10px] uppercase tracking-[0.25em] text-cream/35">{label}</dt>
                    <dd className="mt-1.5 text-sm text-cream">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <input
        type="text"
        name="website"
        data-testid="contact-honeypot"
        value={form.website}
        onChange={update("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="mt-12 flex items-center justify-between gap-4">
        {step > 0 ? (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            data-testid="form-back-btn"
            className="inline-flex items-center gap-2 border border-brand/40 text-brand-300 text-xs uppercase tracking-[0.2em] font-bold px-6 py-4 hover:bg-brand/10 transition-colors duration-300"
          >
            <ArrowLeft size={15} /> {t("fm_back")}
          </button>
        ) : (
          <span />
        )}

        {step < 2 ? (
          <button
            type="button"
            onClick={nextStep}
            disabled={step === 0 && isQuebec}
            data-testid="form-next-btn"
            className="group inline-flex items-center gap-3 bg-brand text-white text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-brand-600 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brand"
          >
            {t("fm_next")}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-btn"
            className="inline-flex items-center gap-3 bg-brand text-white text-xs uppercase tracking-[0.2em] font-bold px-10 py-5 hover:bg-brand-600 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? t("f_submitting") : t("f_submit")}
          </button>
        )}
      </div>

      <p className="mt-6 text-xs text-cream/40 leading-relaxed" data-testid="contact-consent-note">
        {t("f_consent")}
      </p>
    </form>
  );
}
